import type { ComponentPropsWithoutRef, ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  FOCUS_RING,
  SURFACE_INSET,
} from "@/components/ui/surface/surfaceTokens";
import {
  HOME_BODY_TEXT_CLASS,
  HOME_EYEBROW_CLASS,
  HOME_META_TEXT_CLASS,
  HOME_MUTED_TEXT_CLASS,
  HOME_ROW_TITLE_CLASS,
  HOME_SURFACE_INSET,
} from "./homeSurfaces";

/** Normalise table text when rendered inside dialogs or other tinted parents. */
export const HOME_SURFACE_TABLE_TEXT = "text-slate-900 dark:text-slate-100";

export const HOME_SURFACE_TABLE_HEAD_BORDER =
  "border-b border-slate-200 dark:border-slate-700/60";

export const HOME_SURFACE_TABLE_ROW_DIVIDER =
  "divide-y divide-slate-200 dark:divide-slate-800/60";

export const HOME_SURFACE_TABLE_CELL = "px-3 py-3 align-top";

export function HomeSurfaceTableScroll({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={clsx(
        "min-h-0 w-full flex-1 overflow-x-auto overflow-y-auto overscroll-contain rounded-xl",
        HOME_SURFACE_INSET,
        HOME_SURFACE_TABLE_TEXT,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function HomeSurfaceTable({
  className,
  minWidth,
  children,
}: {
  className?: string;
  minWidth?: string;
  children: ReactNode;
}) {
  return (
    <table
      className={clsx("w-full min-w-full text-left text-sm", className)}
      style={minWidth ? { minWidth } : undefined}
    >
      {children}
    </table>
  );
}

export function HomeSurfaceTableHead({
  sticky = false,
  children,
}: {
  sticky?: boolean;
  children: ReactNode;
}) {
  return (
    <thead className={clsx(sticky && clsx("sticky top-0 z-10", SURFACE_INSET))}>
      {children}
    </thead>
  );
}

type SortDirection = "asc" | "desc";

const headerLabelClass = (active: boolean) =>
  clsx(
    HOME_EYEBROW_CLASS,
    active && "text-indigo-600 dark:text-indigo-400",
  );

export function HomeSurfaceTableSortHeader<Column extends string>({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  align = "left",
  testId,
}: {
  label: string;
  column: Column;
  activeColumn: Column;
  direction: SortDirection;
  onSort: (column: Column) => void;
  align?: "left" | "right";
  testId?: string;
}) {
  const active = activeColumn === column;

  return (
    <th
      className={clsx("px-1 py-1", align === "right" && "text-right")}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors",
          "hover:bg-slate-950/5 dark:hover:bg-white/10",
          FOCUS_RING,
          headerLabelClass(active),
          align === "right" && "ml-auto justify-end",
        )}
        onClick={() => onSort(column)}
        data-testid={testId}
      >
        <span>{label}</span>
        {active ? (
          <FontAwesomeIcon
            icon={direction === "asc" ? faChevronUp : faChevronDown}
            className="h-3 w-3 shrink-0 text-indigo-600 dark:text-indigo-400"
          />
        ) : null}
      </button>
    </th>
  );
}

export function HomeSurfaceTableHeaderCell({
  label,
  align = "left",
}: {
  label: string;
  align?: "left" | "right";
}) {
  return (
    <th
      className={clsx(
        "px-3 py-2",
        headerLabelClass(false),
        align === "right" && "text-right",
      )}
    >
      {label}
    </th>
  );
}

export type HomeSurfaceTableCellVariant = "title" | "body" | "meta" | "muted" | "plain";

const CELL_VARIANT_CLASS: Record<HomeSurfaceTableCellVariant, string> = {
  title: HOME_ROW_TITLE_CLASS,
  body: HOME_BODY_TEXT_CLASS,
  meta: HOME_META_TEXT_CLASS,
  muted: HOME_MUTED_TEXT_CLASS,
  plain: "",
};

export function HomeSurfaceTableCell({
  children,
  variant = "body",
  align,
  className,
}: {
  children: ReactNode;
  variant?: HomeSurfaceTableCellVariant;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={clsx(
        HOME_SURFACE_TABLE_CELL,
        CELL_VARIANT_CLASS[variant],
        align === "right" && "text-right",
        className,
      )}
    >
      {children}
    </td>
  );
}

export function HomeSurfaceTableBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <tbody className={clsx(HOME_SURFACE_TABLE_ROW_DIVIDER, className)}>
      {children}
    </tbody>
  );
}
